import type { OrderStatus } from '~entities/order';

import { MoneyPh } from '~shared/ui/money-ph';

import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';

import type { OrderTableData } from '../model/orders-table-data';

const columnHelper = createColumnHelper<OrderTableData>();

type Props = {
  name?: string;
  merchantName?: string;
};

export const statusToTextMap: Record<OrderStatus, string> = {
  NEW: 'New',
  IN_PROGRESS: 'In progress',
  REJECTED: 'Rejected',
  APPROVED: 'Approved',
  CANCELLED: 'Cancelled',
  FINISHED: 'Finished',
};

export enum ColumnId {
  SHOP_ORDER_ID = 'shopOrderId',
  ORDER_NUMBER = 'orderNumber',
  ORDER_DATE = 'orderDate',
  CUSTOMER_NAME = 'customerName',
  STORE_NAME = 'storeName',
  ORDER_STATUS = 'orderStatus',
  ITEMS_NUMBER = 'itemsNumber',
  TOTAL_PRICE = 'totalPrice',
}

export const StoreInfo = ({ name, merchantName }: Props) => {
  if (!name) {
    return <span>–</span>;
  }
  return (
    <div>
      <span
        className={`
          inline-block max-w-full overflow-hidden text-ellipsis
          whitespace-nowrap
        `}
        title={name}
      >
        {name}
      </span>
      <br />
      <span
        className={`
          inline-block max-w-full overflow-hidden text-sm text-ellipsis
          whitespace-nowrap text-black/50
        `}
        title={merchantName}
      >
        {merchantName}
      </span>
    </div>
  );
};

export const columns = [
  columnHelper.accessor((row) => row.orderTimestampUTC, {
    id: ColumnId.ORDER_DATE,
    cell: (data) => {
      const date = new Date(data.getValue());
      return (
        <div>
          <p className="my-0 leading-[24px] text-base-primary">
            {format(date, 'dd-MM-yyyy')}
          </p>
          <p className="my-0 text-sm leading-[16px] text-[rgba(0,0,0,0.54)]">
            {format(date, 'h:mm a')}
          </p>
        </div>
      );
    },
    header: () => <span>Date</span>,
  }),
  columnHelper.accessor((row) => row.shopOrderId, {
    id: ColumnId.SHOP_ORDER_ID,
    cell: (data) => {
      const id = data.getValue();

      return id ? <div className="line-clamp-1 max-w-full">{id}</div> : null;
    },
    header: () => <span>Order number</span>,
  }),
  columnHelper.accessor((row) => row.orderNumber, {
    id: ColumnId.ORDER_NUMBER,
    cell: (data) => <span>{data.getValue()}</span>,
    header: () => <span>Internal order&nbsp;number</span>,
  }),
  columnHelper.accessor((row) => row.customerFullName, {
    id: ColumnId.CUSTOMER_NAME,
    cell: (data) => <span>{data.getValue()}</span>,
    header: () => <span>Customer name</span>,
  }),
  columnHelper.accessor((row) => row.posName, {
    id: ColumnId.STORE_NAME,
    cell: (data) => (
      <div className="max-w-[250px] overflow-hidden text-ellipsis">
        {data.getValue()}
      </div>
    ),
    header: () => <span>Store name</span>,
  }),
  columnHelper.accessor((row) => row.orderStatus, {
    id: ColumnId.ORDER_STATUS,
    cell: (data) => <span>{statusToTextMap[data.getValue()]}</span>,
    header: () => <span>Status</span>,
  }),
  columnHelper.accessor((row) => row.itemsCount, {
    id: ColumnId.ITEMS_NUMBER,
    cell: (data) => <span>{data.getValue()}</span>,
    header: () => <span>Number of&nbsp;items</span>,
  }),
  columnHelper.accessor((row) => row.totalAmount, {
    id: ColumnId.TOTAL_PRICE,
    cell: (data) => (
      <div className="flex justify-end">
        <MoneyPh value={data.getValue()} />
      </div>
    ),
    header: () => <p className="flex-1 text-right">Total</p>,
  }),
];
